import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowDown,
  ArrowLeft,
  Check,
  CheckCheck,
  CircleHelp,
  Copy,
  ImagePlus,
  MessageSquareMore,
  MessageSquareText,
  Send,
  Trash2,
  X,
} from "lucide-react";
import { Chat, Message, MessageAttachment } from "../types";
import { Avatar } from "./shared/Avatar";
import "./ChatWindow.css";

export const usesMobileComposerControls = () =>
  window.matchMedia?.("(pointer: coarse)").matches ||
  (navigator.maxTouchPoints || 0) > 0;

const formatTime = (value: string) =>
  new Date(value).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });

type BackSwipeState = {
  startX: number;
  startY: number;
  currentX: number;
  startTime: number;
  tracking: boolean;
  moved: boolean;
};

const emptyBackSwipe: BackSwipeState = {
  startX: 0,
  startY: 0,
  currentX: 0,
  startTime: 0,
  tracking: false,
  moved: false,
};

type ImageViewerOffset = {
  x: number;
  y: number;
};

type ImageViewerDragState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
};

const IMAGE_VIEWER_MIN_SCALE = 1;
const IMAGE_VIEWER_MAX_SCALE = 5;
const IMAGE_VIEWER_KEYBOARD_STEP = 0.25;
const IMAGE_VIEWER_WHEEL_SPEED = 0.0025;
const IMAGE_VIEWER_DEFAULT_OFFSET: ImageViewerOffset = { x: 0, y: 0 };
export const MAX_TEXT_MESSAGE_LENGTH = 1000;

const messageLength = (value: string) => Array.from(value).length;
const limitMessage = (value: string) =>
  Array.from(value).slice(0, MAX_TEXT_MESSAGE_LENGTH).join("");

const clampImageViewerScale = (value: number) =>
  Math.min(
    IMAGE_VIEWER_MAX_SCALE,
    Math.max(IMAGE_VIEWER_MIN_SCALE, value),
  );

const MessageAuthorAvatar = ({ message, chat }: { message: Message; chat: Chat }) => {
  const isSystem = message.message_type === "system";
  const author = chat.authors?.find((user) => String(user.id) === String(message.sender_id));
  const ownAccount = chat.authors?.find((user) => user.is_our_account);
  const name = isSystem ? "Avito" : author?.name
    || (message.sender === "me" ? ownAccount?.name || "Наш аккаунт Avito" : chat.name);
  const avatar = isSystem ? null : author?.avatar
    || (message.sender === "me" ? ownAccount?.avatar : chat.avatar);
  return (
    <div className="message-avatar" data-copy-enabled="false" role="img" aria-label={`Автор: ${name}`} title={name}>
      <Avatar src={avatar} name={name} />
    </div>
  );
};

const ChatWindow: React.FC<{
  chat?: Chat;
  messages: Message[];
  loading: boolean;
  sending: boolean;
  draft?: string;
  onDraftChange?: (chatId: string, value: string) => void;
  onBack: () => void;
  onSendText: (text: string) => Promise<void>;
  onSendImage: (file: File) => Promise<void>;
  onDeleteMessage?: (messageId: string) => Promise<void>;
  onLoadOlder: () => Promise<number>;
  onOpenQuickReplies?: () => void;
  onOpenTeacherAssistant?: () => void;
  insertRequest?: { id: number; text: string } | null;
}> = ({
  chat,
  messages,
  loading,
  sending,
  draft = "",
  onDraftChange,
  onBack,
  onSendText,
  onSendImage,
  onDeleteMessage,
  onLoadOlder,
  onOpenQuickReplies,
  onOpenTeacherAssistant,
  insertRequest,
}) => {
  const [text, setText] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [sendError, setSendError] = useState("");
  const [messageLimitOpen, setMessageLimitOpen] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedChatLink, setCopiedChatLink] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [initialUnreadCount, setInitialUnreadCount] = useState(0);
  const [readyChatId, setReadyChatId] = useState<string | null>(null);
  const [backSwipe, setBackSwipe] = useState<BackSwipeState>(emptyBackSwipe);
  const [imageViewer, setImageViewer] = useState<MessageAttachment | null>(null);
  const [imageViewerScale, setImageViewerScale] = useState(
    IMAGE_VIEWER_MIN_SCALE,
  );
  const [imageViewerOffset, setImageViewerOffset] =
    useState<ImageViewerOffset>(IMAGE_VIEWER_DEFAULT_OFFSET);
  const [isDraggingImageViewer, setIsDraggingImageViewer] = useState(false);
  const [isZoomingImageViewer, setIsZoomingImageViewer] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const imageViewerOverlayRef = useRef<HTMLDivElement>(null);
  const imageViewerFrameRef = useRef<HTMLDivElement>(null);
  const imageViewerDragRef = useRef<ImageViewerDragState | null>(null);
  const imageViewerScaleRef = useRef(IMAGE_VIEWER_MIN_SCALE);
  const imageViewerGestureStartScaleRef = useRef(IMAGE_VIEWER_MIN_SCALE);
  const imageViewerPendingScaleRef = useRef(IMAGE_VIEWER_MIN_SCALE);
  const imageViewerZoomFrameRef = useRef<number | null>(null);
  const imageViewerWheelEndTimeoutRef = useRef<number | null>(null);
  const seenMessagesRef = useRef<{ chatId: string; ids: Set<string> }>({
    chatId: "",
    ids: new Set(),
  });
  const resetBackSwipe = () => setBackSwipe(emptyBackSwipe);
  const handleBackSwipeStart = (event: React.TouchEvent) => {
    if (window.innerWidth >= 768) return;
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        ".message-input-container, button, a, input, textarea, [role='button']",
      )
    )
      return;
    const touch = event.touches[0];
    setBackSwipe({
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: touch.clientX,
      startTime: Date.now(),
      tracking: true,
      moved: false,
    });
  };
  const handleBackSwipeMove = (event: React.TouchEvent) => {
    if (!backSwipe.tracking) return;
    const touch = event.touches[0];
    const deltaX = touch.clientX - backSwipe.startX;
    const deltaY = Math.abs(touch.clientY - backSwipe.startY);
    const absDeltaX = Math.abs(deltaX);
    if (!backSwipe.moved) {
      if (deltaY > 5 && deltaY >= absDeltaX) {
        resetBackSwipe();
        return;
      }
      if (!(deltaX > 5 && absDeltaX > deltaY + 4)) return;
    }
    event.preventDefault();
    setBackSwipe((current) => ({
      ...current,
      currentX: touch.clientX,
      moved: true,
    }));
  };
  const handleBackSwipeEnd = (event: React.TouchEvent) => {
    if (!backSwipe.tracking) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - backSwipe.startX;
    const deltaY = Math.abs(touch.clientY - backSwipe.startY);
    const duration = Date.now() - backSwipe.startTime;
    if (
      deltaX > deltaY &&
      deltaX >= 30 &&
      duration <= 800 &&
      (deltaX / Math.max(duration, 1)) * 1000 >= 100
    )
      onBack();
    resetBackSwipe();
  };
  const imagePreviewUrl = useMemo(
    () => (image ? URL.createObjectURL(image) : ""),
    [image],
  );
  useEffect(
    () => () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    },
    [imagePreviewUrl],
  );
  // Capture unread count only when switching chats; marking the active chat read must not move its divider.
  useEffect(() => {
    const limitedDraft = limitMessage(draft);
    setHasOlder(true);
    setSendError("");
    setText(limitedDraft);
    setMessageLimitOpen(limitedDraft !== draft);
    if (chat && limitedDraft !== draft) onDraftChange?.(chat.id, limitedDraft);
    setImage(null);
    setIsNearBottom(true);
    setReadyChatId(null);
    setInitialUnreadCount(chat?.unreadCount || 0);
  }, [chat?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const updateText = useCallback(
    (value: string) => {
      const limited = limitMessage(value);
      if (limited !== value) setMessageLimitOpen(true);
      setText(limited);
      if (chat) onDraftChange?.(chat.id, limited);
    },
    [chat, onDraftChange],
  );
  useEffect(() => {
    const area = textareaRef.current;
    if (!area) return;
    area.style.height = "40px";
    if (!text) return;
    area.style.height = `${Math.max(40, Math.min(area.scrollHeight, 160))}px`;
  }, [text]);

  const resetImageViewerZoom = useCallback(() => {
    if (imageViewerZoomFrameRef.current !== null) {
      window.cancelAnimationFrame(imageViewerZoomFrameRef.current);
      imageViewerZoomFrameRef.current = null;
    }
    if (imageViewerWheelEndTimeoutRef.current !== null) {
      window.clearTimeout(imageViewerWheelEndTimeoutRef.current);
      imageViewerWheelEndTimeoutRef.current = null;
    }
    imageViewerScaleRef.current = IMAGE_VIEWER_MIN_SCALE;
    imageViewerPendingScaleRef.current = IMAGE_VIEWER_MIN_SCALE;
    setImageViewerScale(IMAGE_VIEWER_MIN_SCALE);
    setImageViewerOffset(IMAGE_VIEWER_DEFAULT_OFFSET);
    setIsDraggingImageViewer(false);
    setIsZoomingImageViewer(false);
    imageViewerDragRef.current = null;
  }, []);

  const closeImageViewer = useCallback(() => {
    setImageViewer(null);
    resetImageViewerZoom();
  }, [resetImageViewerZoom]);

  const openImageViewer = useCallback(
    (attachment: MessageAttachment) => {
      resetImageViewerZoom();
      setImageViewer(attachment);
    },
    [resetImageViewerZoom],
  );

  const renderImageViewerScale = useCallback((value: number) => {
    const nextScale = clampImageViewerScale(value);
    imageViewerScaleRef.current = nextScale;
    imageViewerPendingScaleRef.current = nextScale;
    if (imageViewerZoomFrameRef.current !== null) return;
    imageViewerZoomFrameRef.current = window.requestAnimationFrame(() => {
      imageViewerZoomFrameRef.current = null;
      const renderedScale = imageViewerPendingScaleRef.current;
      setImageViewerScale(renderedScale);
      if (renderedScale === IMAGE_VIEWER_MIN_SCALE) {
        setImageViewerOffset(IMAGE_VIEWER_DEFAULT_OFFSET);
      }
    });
  }, []);

  const updateImageViewerScale = useCallback(
    (updater: (currentScale: number) => number) => {
      renderImageViewerScale(updater(imageViewerScaleRef.current));
    },
    [renderImageViewerScale],
  );

  useEffect(() => {
    const handleKeyboard = (event: KeyboardEvent) => {
      if (imageViewer) {
        if (event.key === "Escape") {
          event.preventDefault();
          closeImageViewer();
          return;
        }
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.key === "+" || event.key === "=")
        ) {
          event.preventDefault();
          updateImageViewerScale(
            (currentScale) => currentScale + IMAGE_VIEWER_KEYBOARD_STEP,
          );
          return;
        }
        if (
          (event.ctrlKey || event.metaKey) &&
          (event.key === "-" || event.key === "_")
        ) {
          event.preventDefault();
          updateImageViewerScale(
            (currentScale) => currentScale - IMAGE_VIEWER_KEYBOARD_STEP,
          );
          return;
        }
        if ((event.ctrlKey || event.metaKey) && event.key === "0") {
          event.preventDefault();
          resetImageViewerZoom();
        }
        return;
      }
      if (event.key === "Escape") onBack();
    };
    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [
    closeImageViewer,
    imageViewer,
    onBack,
    resetImageViewerZoom,
    updateImageViewerScale,
  ]);

  useEffect(() => {
    if (!imageViewer) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [imageViewer]);

  useEffect(() => {
    const overlay = imageViewerOverlayRef.current;
    if (!overlay || !imageViewer) return undefined;
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      event.stopPropagation();
      setIsZoomingImageViewer(true);
      if (imageViewerWheelEndTimeoutRef.current !== null) {
        window.clearTimeout(imageViewerWheelEndTimeoutRef.current);
      }
      imageViewerWheelEndTimeoutRef.current = window.setTimeout(() => {
        imageViewerWheelEndTimeoutRef.current = null;
        setIsZoomingImageViewer(false);
      }, 120);
      updateImageViewerScale(
        (currentScale) =>
          currentScale - event.deltaY * IMAGE_VIEWER_WHEEL_SPEED,
      );
    };

    const handleGestureStart = (event: Event) => {
      event.preventDefault();
      setIsZoomingImageViewer(true);
      imageViewerGestureStartScaleRef.current = imageViewerScaleRef.current;
    };
    const handleGestureChange = (event: Event) => {
      event.preventDefault();
      const gestureScale = Number(
        (event as Event & { scale?: number }).scale ?? 1,
      );
      if (!Number.isFinite(gestureScale) || gestureScale <= 0) return;
      const nextScale = clampImageViewerScale(
        imageViewerGestureStartScaleRef.current * gestureScale,
      );
      renderImageViewerScale(nextScale);
    };
    const handleGestureEnd = (event: Event) => {
      event.preventDefault();
      setIsZoomingImageViewer(false);
    };

    overlay.addEventListener("wheel", handleWheel, {
      passive: false,
      capture: true,
    });
    overlay.addEventListener("gesturestart", handleGestureStart, {
      passive: false,
    });
    overlay.addEventListener("gesturechange", handleGestureChange, {
      passive: false,
    });
    overlay.addEventListener("gestureend", handleGestureEnd, {
      passive: false,
    });
    return () => {
      overlay.removeEventListener("wheel", handleWheel, true);
      overlay.removeEventListener("gesturestart", handleGestureStart);
      overlay.removeEventListener("gesturechange", handleGestureChange);
      overlay.removeEventListener("gestureend", handleGestureEnd);
    };
  }, [imageViewer, renderImageViewerScale, updateImageViewerScale]);

  const handleImageViewerPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (imageViewerScaleRef.current <= IMAGE_VIEWER_MIN_SCALE) return;
      event.preventDefault();
      event.stopPropagation();
      imageViewerDragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: imageViewerOffset.x,
        originY: imageViewerOffset.y,
      };
      setIsDraggingImageViewer(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [imageViewerOffset],
  );

  const handleImageViewerPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragState = imageViewerDragRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      setImageViewerOffset({
        x: dragState.originX + event.clientX - dragState.startX,
        y: dragState.originY + event.clientY - dragState.startY,
      });
    },
    [],
  );

  const stopImageViewerDrag = useCallback(
    (event?: React.PointerEvent<HTMLDivElement>) => {
      if (event && imageViewerDragRef.current?.pointerId === event.pointerId) {
        event.stopPropagation();
        if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
      }
      imageViewerDragRef.current = null;
      setIsDraggingImageViewer(false);
    },
    [],
  );
  useEffect(() => {
    if (!insertRequest || !chat) return;
    const area = textareaRef.current;
    const start = area?.selectionStart ?? text.length;
    const end = area?.selectionEnd ?? start;
    const before = text.slice(0, start);
    const after = text.slice(end);
    const separator = before && !/\s$/.test(before) ? "\n" : "";
    const requested = `${before}${separator}${insertRequest.text}${after}`;
    const next = limitMessage(requested);
    if (next !== requested) setMessageLimitOpen(true);
    setText(next);
    onDraftChange?.(chat.id, next);
    requestAnimationFrame(() => {
      const pos = before.length + separator.length + insertRequest.text.length;
      area?.focus();
      area?.setSelectionRange(pos, pos);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insertRequest?.id]);
  const firstUnreadIndex = useMemo(() => {
    const count = initialUnreadCount;
    if (!count) return null;
    const incoming = messages
      .map((message, index) => ({ message, index }))
      .filter(({ message }) => message.sender !== "me");
    return incoming[Math.max(0, incoming.length - count)]?.index ?? null;
  }, [messages, initialUnreadCount]);
  const syncBottomState = () => {
    const container = scrollRef.current;
    if (!container) return;
    setIsNearBottom(
      container.scrollHeight - container.scrollTop - container.clientHeight <
        80,
    );
  };
  const scrollToBottom = (behavior: ScrollBehavior = "smooth") =>
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior,
    });
  const beginOwnSend = () => {
    const unreadCount = initialUnreadCount;
    setInitialUnreadCount(0);
    setIsNearBottom(true);
    return unreadCount;
  };
  const finishOwnSend = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom("smooth");
        const pendingImages = Array.from(
          scrollRef.current?.querySelectorAll<HTMLImageElement>(
            ".attachment-image",
          ) || [],
        ).filter((item) => !item.complete);
        pendingImages.forEach((item) => {
          const settle = () => scrollToBottom("smooth");
          item.addEventListener("load", settle, { once: true });
          item.addEventListener("error", settle, { once: true });
        });
      });
    });
  };
  useEffect(() => {
    if (!chat) return;
    const currentIds = new Set(messages.map((message) => message.id));
    if (
      seenMessagesRef.current.chatId !== chat.id ||
      readyChatId !== chat.id
    ) {
      seenMessagesRef.current = { chatId: chat.id, ids: currentIds };
      return;
    }
    if (loading) return;
    const hasNewIncoming = messages.some(
      (message) =>
        message.sender !== "me" &&
        !seenMessagesRef.current.ids.has(message.id),
    );
    seenMessagesRef.current = { chatId: chat.id, ids: currentIds };
    if (!hasNewIncoming) return;
    setInitialUnreadCount(0);
    setIsNearBottom(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom("smooth");
        const pendingImages = Array.from(
          scrollRef.current?.querySelectorAll<HTMLImageElement>(
            ".attachment-image",
          ) || [],
        ).filter((image) => !image.complete);
        pendingImages.forEach((image) => {
          const settle = () => scrollToBottom("smooth");
          image.addEventListener("load", settle, { once: true });
          image.addEventListener("error", settle, { once: true });
        });
      });
    });
  }, [chat, loading, messages, readyChatId]);
  useLayoutEffect(() => {
    if (loading || !chat || readyChatId === chat.id) return;
    let disposed = false;
    let firstFrame = 0;
    let secondFrame = 0;
    let fallback = 0;
    const finishOpening = () => {
      if (disposed) return;
      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => {
          if (disposed) return;
          const container = scrollRef.current;
          if (!container) return;
          if (messages.length) {
            const divider = container.querySelector<HTMLElement>(
              '[data-unread-divider="true"]',
            );
            if (divider) {
              container.scrollTop = Math.max(
                0,
                divider.offsetTop -
                  container.clientHeight / 2 +
                  divider.offsetHeight / 2,
              );
              syncBottomState();
            } else {
              scrollToBottom("auto");
              setIsNearBottom(true);
            }
          }
          setReadyChatId(chat.id);
        });
      });
    };
    const pending = Array.from(
      scrollRef.current?.querySelectorAll<HTMLImageElement>(
        ".attachment-image",
      ) || [],
    ).filter((image) => !image.complete);
    if (!pending.length) finishOpening();
    else {
      let remaining = pending.length;
      const settled = () => {
        remaining -= 1;
        if (remaining === 0) finishOpening();
      };
      pending.forEach((image) => {
        image.addEventListener("load", settled, { once: true });
        image.addEventListener("error", settled, { once: true });
      });
      fallback = window.setTimeout(finishOpening, 5000);
    }
    return () => {
      disposed = true;
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
      window.clearTimeout(fallback);
    };
    // Initial positioning is intentionally tied to the selected chat and its first loaded page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.id, loading, messages, readyChatId]);
  const copyMessage = async (message: Message) => {
    if (!message.text) return;
    await navigator.clipboard.writeText(message.text);
    setCopiedId(message.id);
    window.setTimeout(
      () => setCopiedId((current) => (current === message.id ? null : current)),
      1500,
    );
  };
  const copyChatLink = async () => {
    if (!chat) return;
    const url = new URL(window.location.href);
    url.searchParams.set("chat", chat.id);
    await navigator.clipboard.writeText(url.toString());
    setCopiedChatLink(true);
    window.setTimeout(() => setCopiedChatLink(false), 1500);
  };
  const canDeleteMessage = (message: Message) =>
    message.sender === "me" &&
    message.message_type !== "deleted" &&
    Date.now() - new Date(message.created_at).getTime() <= 3600000;
  const deleteMessage = async (message: Message) => {
    if (!onDeleteMessage || !window.confirm("Удалить сообщение в Avito?"))
      return;
    try {
      await onDeleteMessage(message.id);
    } catch (cause: any) {
      setSendError(
        cause.response?.data?.detail || "Не удалось удалить сообщение",
      );
    }
  };
  const submit = async () => {
    if (sending) return;
    setSendError("");
    if (image) {
      const current = image;
      const unreadCount = beginOwnSend();
      setImage(null);
      try {
        await onSendImage(current);
        finishOwnSend();
      } catch (cause: any) {
        setInitialUnreadCount(unreadCount);
        setImage(current);
        setSendError(
          cause.response?.data?.detail || "Не удалось отправить изображение",
        );
      }
      return;
    }
    const value = text.trim();
    if (!value || !chat) return;
    if (messageLength(value) > MAX_TEXT_MESSAGE_LENGTH) {
      setMessageLimitOpen(true);
      return;
    }
    const unreadCount = beginOwnSend();
    setText("");
    onDraftChange?.(chat.id, "");
    try {
      await onSendText(value);
      finishOwnSend();
    } catch (cause: any) {
      setInitialUnreadCount(unreadCount);
      setText(value);
      onDraftChange?.(chat.id, value);
      setSendError(
        cause.response?.data?.detail || "Не удалось отправить сообщение",
      );
    }
  };
  if (!chat)
    return (
      <div className="chat-window empty">
        <div className="chat-empty-slate">
          <div className="chat-empty-slate-panel">
            <div className="chat-empty-slate-icon">
              <MessageSquareMore size={24} />
            </div>
            <h3>Выберите чат</h3>
            <p>Здесь появится переписка с пользователем Avito</p>
          </div>
        </div>
      </div>
    );
  return (
    <div
      className={`chat-window ${backSwipe.moved ? "swipe-active" : ""}`}
      style={
        backSwipe.moved
          ? {
              transform: `translateX(${Math.min(
                Math.max(backSwipe.currentX - backSwipe.startX, 0) * 0.5,
                100,
              )}px)`,
            }
          : undefined
      }
      onTouchStart={handleBackSwipeStart}
      onTouchMove={handleBackSwipeMove}
      onTouchEnd={handleBackSwipeEnd}
      onTouchCancel={resetBackSwipe}
    >
      <header className="chat-window-header">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={20} />
        </button>
        {chat.context?.is_profile ? (
          <div className="avito-chat-heading avito-profile-heading">
            <div className="chat-header-info">
              <h2>{chat.name}</h2>
              <span className="avito-profile-label">В профиль</span>
            </div>
          </div>
        ) : chat.context?.url ? (
          <a
            className="avito-chat-heading"
            href={chat.context.url}
            target="_blank"
            rel="noreferrer"
          >
            {chat.context?.image && (
              <img
                className="avito-listing-image"
                src={chat.context.image}
                alt="Объявление Avito"
              />
            )}
            <div className="chat-header-info">
              <h2>{chat.name}</h2>
              {chat.context?.title && (
                <span className="avito-context-link">{chat.context.title}</span>
              )}
            </div>
          </a>
        ) : (
          <div className="avito-chat-heading">
            {chat.context?.image && (
              <img
                className="avito-listing-image"
                src={chat.context.image}
                alt="Объявление Avito"
              />
            )}
            <div className="chat-header-info">
              <h2>{chat.name}</h2>
              {chat.context?.title && (
                <span className="avito-context-link">{chat.context.title}</span>
              )}
            </div>
          </div>
        )}
        <div className="chat-window-header-actions">
          <button
            className="chat-window-quick-replies-button"
            onClick={onOpenQuickReplies}
            aria-label="Быстрые ответы"
            title="Быстрые ответы"
          >
            <MessageSquareText size={20} />
          </button>
          <button
            className="chat-window-copy-link-button"
            onClick={() => void copyChatLink()}
            aria-label={
              copiedChatLink ? "Ссылка скопирована" : "Копировать ссылку на чат"
            }
            title={
              copiedChatLink ? "Ссылка скопирована" : "Копировать ссылку на чат"
            }
          >
            {copiedChatLink ? <Check size={20} /> : <Copy size={20} />}
          </button>
          {onOpenTeacherAssistant && (
            <button
              className="chat-window-copy-link-button teacher-assistant-open-button"
              onClick={onOpenTeacherAssistant}
              aria-label="Подобрать преподавателя по диалогу"
              title="Подобрать преподавателя по диалогу"
            >
              <CircleHelp size={20} />
            </button>
          )}
        </div>
      </header>
      <div className="messages-container">
        {readyChatId !== chat.id && (
          <div className="messages-loading-overlay" role="status">
            <span className="avito-loading-label">Загрузка чата</span>
            <div className="avito-message-placeholders" aria-hidden="true">{[0,1,2,3].map(i=><div className="avito-message-placeholder" key={i}><span/><span/></div>)}</div>
          </div>
        )}
        <div
          className={`messages ${readyChatId !== chat.id ? "messages-hidden" : ""}`}
          ref={scrollRef}
          onScroll={async (event) => {
            syncBottomState();
            if (event.currentTarget.scrollTop <= 30 && hasOlder) {
              const before = event.currentTarget.scrollHeight;
              const count = await onLoadOlder();
              if (count === 0) setHasOlder(false);
              requestAnimationFrame(() => {
                if (scrollRef.current)
                  scrollRef.current.scrollTop =
                    scrollRef.current.scrollHeight - before;
              });
            }
          }}
        >
          {messages.map((message, index) => (
            <React.Fragment key={message.id}>
              {firstUnreadIndex === index && (
                <div className="unread-divider" data-unread-divider="true">
                  <span className="unread-divider-text">
                    Непрочитанные сообщения
                  </span>
                </div>
              )}
              <div
                className={`message ${message.sender === "me" ? "own" : "other"} ${!["text", "image"].includes(message.message_type) ? "system-message" : ""}`}
              >
                <div className="message-wrapper">
                  <MessageAuthorAvatar message={message} chat={chat} />
                  <div
                    className={`message-content ${message.isSending ? "sending" : ""}`}
                  >
                    {message.attachments?.map((attachment) => (
                      <div className="attachment" key={attachment.url}>
                        <button
                          type="button"
                          className="attachment-image-button"
                          onClick={() => openImageViewer(attachment)}
                          aria-label={`Открыть изображение ${attachment.name}`}
                        >
                          <img
                            className="attachment-image"
                            src={attachment.url}
                            alt="Вложение Avito"
                          />
                        </button>
                      </div>
                    ))}
                    {message.text && (
                      <div className="message-text-window">{message.text}</div>
                    )}
                    <div
                      className={`message-meta ${canDeleteMessage(message) ? "has-delete-action" : ""}`}
                    >
                      <span className="message-time">
                        {formatTime(message.created_at)}
                      </span>
                      {message.sender === "me" && (
                        <span
                          className={`message-read-status ${message.is_read ? "is-read" : ""}`}
                          aria-label={
                            message.is_read ? "Прочитано" : "Отправлено"
                          }
                        >
                          {message.is_read ? (
                            <CheckCheck size={14} />
                          ) : (
                            <Check size={14} />
                          )}
                        </span>
                      )}
                      {message.text && (
                        <span className="message-inline-actions">
                          <button
                            className="message-copy-button"
                            onClick={() => void copyMessage(message)}
                            aria-label={
                              copiedId === message.id
                                ? "Скопировано"
                                : "Копировать"
                            }
                            title="Копировать"
                          >
                            {copiedId === message.id ? (
                              <Check size={14} />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                          {canDeleteMessage(message) && (
                            <button
                              className="message-delete-button"
                              onClick={() => void deleteMessage(message)}
                              aria-label="Удалить"
                              title="Удалить"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                    {message.error && (
                      <div className="avito-send-error">{message.error}</div>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
        {!isNearBottom && (
          <button
            type="button"
            className="scroll-to-bottom"
            onClick={() => scrollToBottom()}
            aria-label="В конец"
            title="В конец"
          >
            <ArrowDown size={18} />
          </button>
        )}
      </div>
      {image && (
        <div className="attachments-preview">
          <div className="attachment-preview">
            <img src={imagePreviewUrl} alt="" />
            <span>{image.name}</span>
            <button
              className="remove-attachment"
              onClick={() => setImage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {sendError && (
        <div className="avito-composer-error" role="alert">
          {sendError}
        </div>
      )}
      {messageLimitOpen && (
        <div className="message-limit-overlay" role="presentation">
          <div className="message-limit-dialog" role="dialog" aria-modal="true" aria-labelledby="message-limit-title">
            <h3 id="message-limit-title">Сообщение слишком длинное</h3>
            <p>
              В одном сообщении Avito можно отправить максимум {MAX_TEXT_MESSAGE_LENGTH.toLocaleString("ru-RU")} символов. Лишняя часть не добавлена — проверьте текст или разделите его на несколько сообщений.
            </p>
            <button type="button" onClick={() => setMessageLimitOpen(false)}>Понятно</button>
          </div>
        </div>
      )}
      {imageViewer && (
        <div
          ref={imageViewerOverlayRef}
          className="fullscreen-overlay"
          onClick={closeImageViewer}
        >
          <div
            className="fullscreen-content"
            role="dialog"
            aria-modal="true"
            aria-label="Просмотр фото"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              ref={imageViewerFrameRef}
              className={`fullscreen-image-frame ${imageViewerScale > IMAGE_VIEWER_MIN_SCALE ? "is-zoomed" : ""} ${isDraggingImageViewer ? "is-dragging" : ""} ${isZoomingImageViewer ? "is-zooming" : ""}`}
              style={{
                transform: `translate3d(${imageViewerOffset.x}px, ${imageViewerOffset.y}px, 0) scale(${imageViewerScale})`,
              }}
              onPointerDown={handleImageViewerPointerDown}
              onPointerMove={handleImageViewerPointerMove}
              onPointerUp={stopImageViewerDrag}
              onPointerCancel={stopImageViewerDrag}
              onPointerLeave={stopImageViewerDrag}
              onClick={(event) => event.stopPropagation()}
            >
              <img
                className="fullscreen-image"
                src={imageViewer.url}
                alt={imageViewer.name || "Фото Avito"}
              />
            </div>
            <button
              type="button"
              className="fullscreen-close"
              onClick={closeImageViewer}
              aria-label="Закрыть просмотр фото"
            >
              <X size={30} />
            </button>
          </div>
        </div>
      )}
      <div className="message-input-container">
        <div className="message-input">
          <div className="input-controls">
            <button
              className="attach-button"
              onClick={() => fileRef.current?.click()}
              aria-label="Прикрепить изображение"
            >
              <ImagePlus size={20} />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/bmp,image/heic,image/heif"
              hidden
              onChange={(e) => setImage(e.target.files?.[0] || null)}
            />
          </div>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => updateText(e.target.value)}
            placeholder="Сообщение"
            disabled={sending || Boolean(image)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !usesMobileComposerControls()
              ) {
                e.preventDefault();
                void submit();
              }
            }}
          />
          {text && (
            <span className={`message-length ${messageLength(text) >= 900 ? "is-near-limit" : ""}`} aria-live="polite">
              {messageLength(text)} / {MAX_TEXT_MESSAGE_LENGTH}
            </span>
          )}
          <button
            className="send-button"
            onClick={() => void submit()}
            disabled={sending || (!text.trim() && !image)}
            aria-label="Отправить"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
